import { apiGet, apiPost, apiPut, apiDelete } from '../api-client'

// ==================== SPONSORS ====================

export interface AdvertiserSponsorItem {
  sponsorId: string
  name: string
  logo?: string
  website?: string
  description?: string
  tier?: 'gold' | 'diamond' | 'platinum' | string
  isVerified?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface AdvertiserSponsorsResponse {
  items: AdvertiserSponsorItem[]
  nextToken?: string
  totalCount: number
}

export async function listSponsors(params?: {
  limit?: number
  nextToken?: string
}): Promise<AdvertiserSponsorsResponse> {
  const search = new URLSearchParams()
  if (params?.limit != null) search.set('limit', String(params.limit))
  if (params?.nextToken) search.set('nextToken', params.nextToken)
  const qs = search.toString()
  return apiGet<AdvertiserSponsorsResponse>(qs ? `/admin/sponsors?${qs}` : '/admin/sponsors')
}

export async function createSponsor(body: {
  name: string
  logo?: string
  website?: string
  description?: string
  tier?: string
  isVerified?: boolean
}): Promise<AdvertiserSponsorItem> {
  return apiPost<AdvertiserSponsorItem>('/admin/sponsors', body)
}

export async function getSponsor(sponsorId: string): Promise<AdvertiserSponsorItem> {
  return apiGet<AdvertiserSponsorItem>(`/admin/sponsors/${sponsorId}`)
}

export async function updateSponsor(
  sponsorId: string,
  body: Partial<AdvertiserSponsorItem>,
): Promise<AdvertiserSponsorItem> {
  return apiPut<AdvertiserSponsorItem>(`/admin/sponsors/${sponsorId}`, body)
}

export async function deleteSponsor(sponsorId: string): Promise<{ deleted: boolean }> {
  return apiDelete<{ deleted: boolean }>(`/admin/sponsors/${sponsorId}`)
}

// ==================== ADS ====================

export interface AdvertiserAdItem {
  adId: string
  type: 'preroll_video' | 'display_image'
  sponsorId?: string | null
  sponsor?: AdvertiserSponsorItem | null
  videoUrl?: string | null
  imageUrl?: string | null
  skipAfterSeconds?: number
  clickUrl?: string
  name?: string
  status?: 'active' | 'paused' | 'draft' | string
  createdAt?: string
  updatedAt?: string
}

export interface AdvertiserAdsResponse {
  items: AdvertiserAdItem[]
  nextToken?: string
  totalCount: number
}

export async function listAds(params?: {
  limit?: number
  nextToken?: string
}): Promise<AdvertiserAdsResponse> {
  const search = new URLSearchParams()
  if (params?.limit != null) search.set('limit', String(params.limit))
  if (params?.nextToken) search.set('nextToken', params.nextToken)
  const qs = search.toString()
  return apiGet<AdvertiserAdsResponse>(qs ? `/admin/ads?${qs}` : '/admin/ads')
}

export async function createAd(body: {
  type: 'preroll_video' | 'display_image'
  sponsorId?: string | null
  videoUrl?: string | null
  imageUrl?: string | null
  skipAfterSeconds?: number
  clickUrl?: string
  name?: string
  status?: string
}): Promise<AdvertiserAdItem> {
  return apiPost<AdvertiserAdItem>('/admin/ads', body)
}

export async function getAd(adId: string): Promise<AdvertiserAdItem> {
  return apiGet<AdvertiserAdItem>(`/admin/ads/${adId}`)
}

export async function updateAd(
  adId: string,
  body: Partial<AdvertiserAdItem>,
): Promise<AdvertiserAdItem> {
  return apiPut<AdvertiserAdItem>(`/admin/ads/${adId}`, body)
}

export async function deleteAd(adId: string): Promise<{ deleted: boolean }> {
  return apiDelete<{ deleted: boolean }>(`/admin/ads/${adId}`)
}

// ==================== FILMS (ad assignment) ====================

export interface AdvertiserFilmItem {
  filmId: string
  id?: string
  title?: string
  genre?: string
  monetization?: string
  prerollAdId?: string | null
  displayAdId?: string | null
}

export interface AdvertiserFilmsResponse {
  items: AdvertiserFilmItem[]
  nextToken?: string
  totalCount?: number
}

export async function listFilmsForAdAssignment(params?: {
  limit?: number
  nextToken?: string
}): Promise<AdvertiserFilmsResponse> {
  const search = new URLSearchParams()
  if (params?.limit != null) search.set('limit', String(params.limit))
  if (params?.nextToken) search.set('nextToken', params.nextToken)
  const qs = search.toString()
  return apiGet<AdvertiserFilmsResponse>(qs ? `/films?${qs}` : '/films')
}

export async function assignAdToFilm(
  filmId: string,
  body: { prerollAdId?: string | null; displayAdId?: string | null },
): Promise<unknown> {
  return apiPut(`/admin/films/${filmId}`, body)
}

// ==================== SPONSOR DEALS ====================

export type SponsorDealStatus = 'pending' | 'accepted' | 'rejected' | 'active' | 'completed'

export interface SponsorDealItem {
  dealId: string
  sponsorId: string
  creatorId: string
  filmId: string
  amount: number
  status: SponsorDealStatus
  terms?: string
  adId?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface SponsorDealsResponse {
  items: SponsorDealItem[]
  nextToken?: string
  totalCount: number
}

export async function listSponsorDeals(params?: {
  limit?: number
  nextToken?: string
  status?: SponsorDealStatus
}): Promise<SponsorDealsResponse> {
  const search = new URLSearchParams()
  if (params?.limit != null) search.set('limit', String(params.limit))
  if (params?.nextToken) search.set('nextToken', params.nextToken)
  if (params?.status) search.set('status', params.status)
  const qs = search.toString()
  return apiGet<SponsorDealsResponse>(qs ? `/admin/sponsor-deals?${qs}` : '/admin/sponsor-deals')
}

export async function createSponsorDeal(body: {
  sponsorId: string
  creatorId: string
  filmId: string
  amount: number
  terms?: string
  adId?: string | null
}): Promise<SponsorDealItem> {
  return apiPost<SponsorDealItem>('/admin/sponsor-deals', body)
}

export async function updateSponsorDeal(
  dealId: string,
  body: Partial<SponsorDealItem>,
): Promise<SponsorDealItem> {
  return apiPut<SponsorDealItem>(`/admin/sponsor-deals/${dealId}`, body)
}

// ==================== AD ANALYTICS ====================

export interface AdImpressionStats {
  adId: string
  totalImpressions: number
  completions: number
  clicks: number
  completionRate: number
  clickThroughRate: number
}

export interface AdvertiserAnalyticsResponse {
  totalImpressions: number
  totalCompletions: number
  totalClicks: number
  overallCompletionRate: number
  overallCtr: number
  byAd: AdImpressionStats[]
}

export async function getAdvertiserAnalytics(params?: {
  range?: '7d' | '30d'
}): Promise<AdvertiserAnalyticsResponse> {
  const search = new URLSearchParams()
  if (params?.range) search.set('range', params.range)
  const qs = search.toString()
  return apiGet<AdvertiserAnalyticsResponse>(qs ? `/admin/analytics/ads?${qs}` : '/admin/analytics/ads')
}
